// Toggle admin do auto-assign de líder fora do expediente (painel Tecnologia).
// Liga/desliga o sweeper que atribui líder sozinho à noite e no fim de semana.
// Persiste em system_settings.auto_assign_enabled via /api/admin/settings/auto-assign.
// IMPORTANTE: a JANELA (horários) é FONTE ÚNICA no backend — esta UI NÃO replica
// horário; consome `windowOpen` + `nextBoundaryMs` da API e só renderiza o countdown.
import { useState, useEffect, useCallback } from 'react'
import { FiUserPlus, FiClock, FiAlertTriangle } from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../lib/api'

const pad = (n: number) => String(n).padStart(2, '0')

function fmtRemaining(ms: number): string {
  if (ms < 0) ms = 0
  const totalSec = Math.floor(ms / 1000)
  const d = Math.floor(totalSec / 86400)
  const h = Math.floor((totalSec % 86400) / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  return d > 0 ? `${d}d ${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(h)}:${pad(m)}:${pad(s)}`
}

function boundaryLabel(at: Date, now: Date): string {
  const hhmm = `${pad(at.getHours())}:${pad(at.getMinutes())}`
  if (at.toDateString() === now.toDateString()) return `hoje ${hhmm}`
  const wd = at.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '')
  return `${wd} ${hhmm}`
}

export function AutoAssignToggle() {
  const { authenticatedFetch } = useAuth()
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [windowOpen, setWindowOpen] = useState<boolean | null>(null)
  const [boundaryMs, setBoundaryMs] = useState<number | null>(null)
  const [skew, setSkew] = useState(0)            // serverNow - clientNow no fetch (corrige relógio)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [, setTick] = useState(0)

  // Lê estado + janela do backend (fonte única dos horários). skew alinha o
  // countdown ao relógio do servidor, não ao do navegador.
  const refresh = useCallback(async () => {
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.AUTO_ASSIGN)
      const d = await r.json()
      if (!d.success) throw new Error()
      setEnabled(d.enabled)
      setWindowOpen(!!d.windowOpen)
      setBoundaryMs(typeof d.nextBoundaryMs === 'number' ? d.nextBoundaryMs : null)
      if (typeof d.serverNowMs === 'number') setSkew(d.serverNowMs - Date.now())
    } catch { setError('Falha ao ler config') }
  }, [authenticatedFetch])

  useEffect(() => { refresh() }, [refresh])

  // Tick de 1s só quando ligado. Quando o countdown chega a zero, a janela virou
  // → re-busca o novo estado/boundary do backend (sem replicar a regra aqui).
  useEffect(() => {
    if (!enabled) return
    const id = setInterval(() => {
      if (boundaryMs !== null && (boundaryMs - (Date.now() + skew)) <= 0) refresh()
      else setTick(t => t + 1)
    }, 1000)
    return () => clearInterval(id)
  }, [enabled, boundaryMs, skew, refresh])

  const toggle = async () => {
    if (saving || enabled === null) return
    setSaving(true)
    setError(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.AUTO_ASSIGN, {
        method: 'PUT',
        body: JSON.stringify({ enabled: !enabled }),
      })
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || `HTTP ${r.status}`)
      setEnabled(d.enabled)
      setWindowOpen(!!d.windowOpen)
      setBoundaryMs(typeof d.nextBoundaryMs === 'number' ? d.nextBoundaryMs : null)
      if (typeof d.serverNowMs === 'number') setSkew(d.serverNowMs - Date.now())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao salvar')
    } finally {
      setSaving(false)
    }
  }

  const now = new Date(Date.now() + skew)
  const isOpen = !!windowOpen
  const boundaryAt = boundaryMs !== null ? new Date(boundaryMs) : null
  const remaining = boundaryAt ? fmtRemaining(boundaryAt.getTime() - now.getTime()) : '—'

  return (
    <div className="glass-panel neu-card rounded-2xl p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <FiUserPlus className="w-4 h-4 text-primary shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground leading-tight">Auto-assign de líder</div>
            <div className="text-[11px] text-muted-foreground">Atribui líder sozinho fora do expediente</div>
          </div>
        </div>
        <button
          role="switch" aria-checked={!!enabled}
          onClick={toggle}
          disabled={saving || enabled === null}
          title="Quando ligado, o bot atribui o líder mais adequado sozinho fora do expediente (à noite e no fim de semana). Em horário comercial a atribuição segue manual. Os horários da janela são definidos no backend."
          className={`relative w-11 h-6 rounded-full shrink-0 transition-colors duration-200 disabled:opacity-50 neu-switch ${enabled ? 'bg-primary' : 'bg-muted neu-switch-off'}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full neu-knob transition-transform duration-200 ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
        </button>
      </div>
      {enabled && (
        <div className={`flex items-center gap-1 text-[10px] ${isOpen ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
          <FiClock className="w-3 h-3 shrink-0" />
          <span>{isOpen ? 'Janela ABERTA agora — atribuindo automaticamente' : 'Fora da janela agora — atribuição manual (horário comercial)'}</span>
        </div>
      )}
      {enabled && (
        <div
          className={`flex items-center gap-1 text-[10px] ${isOpen ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground'}`}
          title={boundaryAt ? (isOpen ? `A janela fecha em ${boundaryLabel(boundaryAt, now)}` : `O auto-assign liga sozinho em ${boundaryLabel(boundaryAt, now)}`) : ''}
        >
          <FiClock className="w-3 h-3 shrink-0" />
          <span>
            {isOpen ? 'Encerra em ' : 'Ativa automaticamente em '}
            <span className="font-mono font-semibold tabular-nums">{remaining}</span>
            {boundaryAt && <span className="text-muted-foreground"> ({boundaryLabel(boundaryAt, now)})</span>}
          </span>
        </div>
      )}
      {enabled && (
        <div className="flex items-start gap-1 text-[10px] text-amber-600 dark:text-amber-400">
          <FiAlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>Só atribui a líder com histórico recente (últimos 90d) dentro de 60 km; sem candidata, segura o lead pro humano.</span>
        </div>
      )}
      {error && <div className="text-[10px] text-red-600 dark:text-red-400">{error}</div>}
    </div>
  )
}
